import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../../../context/AppContext";
import useHttp from "../../../hooks/useHttp";

export interface WorkflowEnumItem {
    code: string;
    idleLabel: string;
    activeLabel: string;
    order: number;
    meta: {
        group?: string;
        kind?: string;
        description?: string;
        config?: Record<string, unknown>;
        /** Trigger codes (e.g. "Trigger.Message.Inbound", "Trigger.Message.Outbound") this condition/action supports. If absent, supports all triggers. */
        supportedTriggers?: string[];
    };
}

export interface WorkflowEnumOption {
    value: string;
    label: string;
    desc?: string;
}

export interface WorkflowEnumsData {
    triggerTypes: WorkflowEnumItem[];
    actionTypes: WorkflowEnumItem[];
    conditionTypes: WorkflowEnumItem[];
    scheduleOptions?: WorkflowEnumOption[];
}

export interface UseWorkflowEnumsResult {
    conditionTypes: WorkflowEnumItem[];
    actionTypes: WorkflowEnumItem[];
    triggerTypes: WorkflowEnumItem[];
    scheduleOptions: WorkflowEnumOption[];
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

const DEFAULT_ENUMS: WorkflowEnumsData = {
    triggerTypes: [],
    actionTypes: [],
    conditionTypes: [],
    scheduleOptions: []
};

export function useWorkflowEnums(): UseWorkflowEnumsResult {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const [data, setData] = useState<WorkflowEnumsData>(DEFAULT_ENUMS);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const loadEnums = useCallback(async () => {
        if (!appProperties) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetchData("workflows/enums", "GET", null, appProperties);
            if (!response) {
                setData(DEFAULT_ENUMS);
                return;
            }
            const parsed = typeof response === "string" ? JSON.parse(response) : response;
            const apiBody = parsed?.data ?? parsed;
            const payload = apiBody?.data ?? apiBody;
            if (payload && typeof payload === "object") {
                const conditionTypes = Array.isArray(payload.conditionTypes) ? payload.conditionTypes : [];
                const runScheduleCondition = conditionTypes.find(
                    (c: WorkflowEnumItem) => c.code === "Condition.Time.RunSchedule"
                );
                const scheduleFromConfig = runScheduleCondition?.meta?.config?.schedule as
                    | Array<{ code?: string; idleLabel?: string; meta?: { description?: string } }>
                    | undefined;
                const scheduleOptions: WorkflowEnumOption[] = Array.isArray(scheduleFromConfig)
                    ? scheduleFromConfig.map((s) => {
                          const code = s.code ?? "";
                          const value = code === "Custom" ? "SpecificHours" : code;
                          const label =
                              code === "Custom"
                                  ? "Specific Hours"
                                  : (s.idleLabel ?? s.code ?? "");
                          return { value, label, desc: s.meta?.description };
                      })
                    : [];

                const rawActionTypes = Array.isArray(payload.actionTypes) ? payload.actionTypes : [];
                const actionTypes = rawActionTypes.filter(
                    (a: WorkflowEnumItem) =>
                        a.code !== "Action.FlowControl.Delay" &&
                        a.code !== "Action.Crm.AssignContact" &&
                        a.code !== "Action.Crm.ManageConversationStatus"
                );

                const conditionTypesWithNumberGroup = conditionTypes.map((ct: WorkflowEnumItem) => {
                    if (ct.code === "Condition.Message.NumberMatch") {
                        return { ...ct, meta: { ...ct.meta, group: "Number" } };
                    }
                    return ct;
                });

                setData({
                    triggerTypes: Array.isArray(payload.triggerTypes) ? payload.triggerTypes : [],
                    actionTypes,
                    conditionTypes: conditionTypesWithNumberGroup,
                    scheduleOptions: scheduleOptions.length > 0 ? scheduleOptions : undefined
                });
            } else {
                setData(DEFAULT_ENUMS);
            }
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            setData(DEFAULT_ENUMS);
        } finally {
            setIsLoading(false);
        }
    }, [appProperties, fetchData]);

    useEffect(() => {
        loadEnums();
    }, [loadEnums]);

    const scheduleOptions =
        data.scheduleOptions && data.scheduleOptions.length > 0
            ? data.scheduleOptions
            : [
                  { value: "24_7", label: "24/7", desc: "Always active" },
                  { value: "BusinessHours", label: "Business Hours", desc: "Mon-Fri, 9am-5pm" },
                  { value: "OutsideBusinessHours", label: "Outside Business Hours", desc: "Evenings & weekends" },
                  { value: "SpecificHours", label: "Specific Hours", desc: "Custom time range" }
              ];

    return {
        conditionTypes: data.conditionTypes,
        actionTypes: data.actionTypes,
        triggerTypes: data.triggerTypes,
        scheduleOptions,
        isLoading,
        error,
        refetch: loadEnums
    };
}
